/**
 * System Search Component
 * Role-aware search for quick navigation across the system
 * Searches projects, tasks, users, reports, and settings
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Search, X, Zap, FolderKanban, CheckSquare, Users, BarChart3, Settings } from 'lucide-react';
import { Project, Task, User } from '../types';
import { getUserProfilePath } from '../utils/userProfile';
import { cn } from './ui/utils';

interface SearchResult {
  id: string;
  title: string;
  type: 'project' | 'task' | 'user' | 'report' | 'setting';
  icon: React.ReactNode;
  href: string;
  description?: string;
}

interface SystemSearchProps {
  projects: Project[];
  tasks: Task[];
  users: User[];
  currentUser: User;
  className?: string;
  placeholder?: string;
}

export const SystemSearch: React.FC<SystemSearchProps> = ({
  projects,
  tasks,
  users,
  currentUser,
  className,
  placeholder = 'Search projects, tasks, people...',
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];

    const q = query.toLowerCase();
    const results: SearchResult[] = [];

    // Search projects (all users can see)
    projects.forEach((project) => {
      if (project.name.toLowerCase().includes(q)) {
        results.push({
          id: `proj-${project.id}`,
          title: project.name,
          type: 'project',
          icon: <FolderKanban className="w-4 h-4" />,
          href: `/app/projects/${project.id}`,
          description: project.description?.slice(0, 50),
        });
      }
    });

    // Search tasks (only if user has access)
    tasks.forEach((task) => {
      if (task.title.toLowerCase().includes(q)) {
        const project = projects.find(p => p.id === task.projectId);
        if (project) {
          results.push({
            id: `task-${task.id}`,
            title: task.title,
            type: 'task',
            icon: <CheckSquare className="w-4 h-4" />,
            href: `/app/projects/${task.projectId}/task/${task.id}`,
            description: `in ${project.name}`,
          });
        }
      }
    });

    // Search users (team members only for standard users)
    users.forEach((user) => {
      if (user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q)) {
        // Limit user search based on role
        if (
          currentUser.role === 'admin' ||
          currentUser.role === 'owner' ||
          currentUser.role === 'project_lead'
        ) {
          results.push({
            id: `user-${user.id}`,
            title: user.name,
            type: 'user',
            icon: <Users className="w-4 h-4" />,
            href: getUserProfilePath(user, currentUser),
            description: user.email,
          });
        }
      }
    });

    // Add quick shortcuts based on role
    const shortcuts: SearchResult[] = [];

    if (currentUser.role === 'admin' || currentUser.role === 'owner') {
      shortcuts.push({
        id: 'settings-org',
        title: 'Organization Settings',
        type: 'setting',
        icon: <Settings className="w-4 h-4" />,
        href: '/app/settings/organization',
      });
      shortcuts.push({
        id: 'settings-users',
        title: 'Manage Users',
        type: 'setting',
        icon: <Users className="w-4 h-4" />,
        href: '/app/allowed-users',
      });
    }

    if (
      currentUser.role === 'admin' ||
      currentUser.role === 'owner' ||
      currentUser.role === 'project_lead'
    ) {
      shortcuts.push({
        id: 'reports',
        title: 'Team Reports',
        type: 'report',
        icon: <BarChart3 className="w-4 h-4" />,
        href: '/app/team-reports',
      });
    }

    shortcuts.push({
      id: 'profile',
      title: 'My Profile',
      type: 'setting',
      icon: <Settings className="w-4 h-4" />,
      href: '/app/profile',
    });

    // Filter shortcuts that match query
    const filteredShortcuts = shortcuts.filter(
      (s) =>
        s.title.toLowerCase().includes(q) &&
        results.length < 8 &&
        !results.find((r) => r.id === s.id)
    );

    return [...results.slice(0, 8 - filteredShortcuts.length), ...filteredShortcuts].slice(0, 8);
  }, [query, projects, tasks, users, currentUser.role]);

  const handleSelect = (href: string) => {
    navigate(href);
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div className={cn('relative w-full', className)}>
      <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2 shadow-sm transition-colors focus-within:border-border focus-within:bg-white dark:focus-within:bg-muted/40">
        <Search className="h-4 w-4 text-muted-foreground flex-shrink-0 transition-colors" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="flex-1 min-w-0 border-none bg-transparent text-sm font-medium text-muted-foreground placeholder:text-muted-foreground outline-none dark:placeholder:text-muted-foreground"
          aria-label="System search"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
            className="flex h-6 w-6 items-center justify-center rounded bg-transparent p-0 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-muted-foreground dark:hover:bg-muted/40 dark:hover:text-muted-foreground"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && query && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-xl border border-border bg-white shadow-lg bg-muted/40">
          {searchResults.length > 0 ? (
            <div className="flex flex-col">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(result.href)}
                  className="flex w-full cursor-pointer items-center gap-3 border-border bg-white p-3 text-left transition-colors hover:bg-muted/40 last:border-b-0 bg-muted/40 dark:hover:bg-muted/40"
                >
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-muted/40 text-muted-foreground">{result.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-muted-foreground">{result.title}</div>
                    {result.description && (
                      <div className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">{result.description}</div>
                    )}
                  </div>
                  <div className="flex-shrink-0 rounded bg-primary/10 px-2 py-1 text-xs font-medium capitalize text-primary">{result.type}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 p-12 text-muted-foreground">
              <Zap className="w-5 h-5 opacity-50" />
              <p className="text-sm font-medium">No results found</p>
              <span className="text-xs text-muted-foreground">Try searching for projects, tasks, or team members</span>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setQuery('');
          }}
        />
      )}
    </div>
  );
};

export default SystemSearch;
